/**
 * Owner: Haselton Baker Risk Group, LLC
 * Copyright All Rights Reserved
 */
// @flow
import type { Element } from 'react';

import React from 'react';
import { Form } from 'reactstrap';
import {
  COMPONENT_IMPORTANCE_FACTOR,
  DESIGN_1_SEC_SPECTRAL_ACC,
  DESIGN_SHORT_PERIOD_SPECTRAL_ACC,
  DETAIL_LEVEL_DIR_1,
  DETAIL_LEVEL_DIR_2,
  DRIFT_LIMIT_DIR_1,
  DRIFT_LIMIT_DIR_2,
  INCLUDE_RETROFIT,
  MAPPED_1_SEC_SPECTRAL_ACC,
  MAPPED_SHORT_PERIOD_SPECTRAL_ACC,
  NON_STRUCTURAL_RETROFIT_CODE_YEAR,
  NON_STRUCTURAL_RETROFIT_LEVEL,
  NON_STRUCTURAL_RETROFIT_YEAR,
  RISK_CATEGORY,
  SEISMIC_IMPORTANCE_FACTOR,
  STRUCTURAL_RETROFIT_CODE_YEAR,
  STRUCTURAL_RETROFIT_LEVEL,
  STRUCTURAL_RETROFIT_YEAR,
} from '@hbrisk/sp3-risk-model-support/models/app/attributes/names/index.js';
import BackButton from '#components/pages/Models/Model/sections/support/buttons/BackButton.jsx';
import NextButton from '#components/pages/Models/Model/sections/support/buttons/NextButton.jsx';
import Tip from '#components/support/tooltips/Tip/index.jsx';
import {
  Field,
  FormGroup,
  Fieldset,
  FormGroupCol as Col,
  FormGroupRow as Row,
  Label,
} from '#components/pages/Models/Model/support/form/index.js';
import FieldVisibilityToggle from '#components/pages/Models/Model/sections/support/forms/FieldVisibilityToggle/index.js';

type Props = {|
  form: string,
  fieldsByName: Object,
  heading: string,
  onBack: Function,
  onNext: Function,
  structuralRetrofitYearIsVisible: boolean,
  nonstructuralRetrofitYearIsVisible: boolean,
|};

const BuildingDesignInfoSection = (props: Props): Element<'div'> => {
  const {
    form,
    fieldsByName,
    heading,
    onBack,
    onNext,
    structuralRetrofitYearIsVisible,
    nonstructuralRetrofitYearIsVisible,
  } = props;
  return (
    <div>
      <h2>{heading}</h2>
      <Form noValidate>
        <Fieldset>
          <legend>Building Design Information</legend>
          <FormGroup form={form} fields={[DETAIL_LEVEL_DIR_1, DETAIL_LEVEL_DIR_2]}>
            <Row>
              <Col lg="4">
                Level of Detailing <Tip tipId="detailLevel" />
              </Col>
            </Row>
            <Row>
              <Col lg="6" className="pl-4">
                <Field field={fieldsByName[DETAIL_LEVEL_DIR_1]} />
              </Col>
            </Row>
            <Row>
              <Col lg="6" className="pl-4">
                <Field field={fieldsByName[DETAIL_LEVEL_DIR_2]} />
              </Col>
            </Row>
          </FormGroup>
          <FormGroup form={form} fields={[RISK_CATEGORY]} row>
            <Col lg="6">
              <Field field={fieldsByName[RISK_CATEGORY]} />
            </Col>
          </FormGroup>
          <FormGroup form={form} fields={[SEISMIC_IMPORTANCE_FACTOR]} row>
            <Col lg="6">
              <Label field={fieldsByName[SEISMIC_IMPORTANCE_FACTOR]}>
                <span>
                  Seismic Importance Factor, I<small>e</small>
                </span>
              </Label>
              <Field excludeLabel field={fieldsByName[SEISMIC_IMPORTANCE_FACTOR]} />
            </Col>
          </FormGroup>
          <FormGroup form={form} fields={[DRIFT_LIMIT_DIR_1, DRIFT_LIMIT_DIR_2]}>
            <Row>
              <Col lg="4">
                Drift Limit <Tip tipId="driftLimit" />
              </Col>
            </Row>
            <Row>
              <Col lg="6" className="pl-4">
                <Field field={fieldsByName[DRIFT_LIMIT_DIR_1]} />
              </Col>
            </Row>
            <Row>
              <Col lg="6" className="pl-4">
                <Field field={fieldsByName[DRIFT_LIMIT_DIR_2]} />
              </Col>
            </Row>
          </FormGroup>
          <FormGroup form={form} fields={[COMPONENT_IMPORTANCE_FACTOR]} row>
            <Col lg="6">
              <Label field={fieldsByName[COMPONENT_IMPORTANCE_FACTOR]}>
                <span>
                  Component Importance Factor, I<small>p</small>
                </span>
              </Label>
              <Field
                excludeLabel
                columnSize="4"
                field={fieldsByName[COMPONENT_IMPORTANCE_FACTOR]}
              />
            </Col>
          </FormGroup>
        </Fieldset>
        <Fieldset
          form={form}
          fields={[
            MAPPED_SHORT_PERIOD_SPECTRAL_ACC,
            MAPPED_1_SEC_SPECTRAL_ACC,
            DESIGN_SHORT_PERIOD_SPECTRAL_ACC,
            DESIGN_1_SEC_SPECTRAL_ACC,
          ]}
        >
          <legend>
            Design Spectral Acceleration <Tip tipId="designSpectralAcceleration" />
          </legend>
          <FormGroup form={form} fields={[MAPPED_SHORT_PERIOD_SPECTRAL_ACC]} row>
            <Col lg="6">
              <Label field={fieldsByName[MAPPED_SHORT_PERIOD_SPECTRAL_ACC]}>
                <span>
                  Mapped Short Period Spectral Acceleration (S
                  <small>s</small>)
                </span>
              </Label>
              <Field excludeLabel field={fieldsByName[MAPPED_SHORT_PERIOD_SPECTRAL_ACC]} />
            </Col>
          </FormGroup>
          <FormGroup form={form} fields={[MAPPED_1_SEC_SPECTRAL_ACC]} row>
            <Col lg="6">
              <Label field={fieldsByName[MAPPED_1_SEC_SPECTRAL_ACC]}>
                <span>
                  Mapped 1-Second Spectral Acceleration (S
                  <small>1</small>)
                </span>
              </Label>
              <Field excludeLabel field={fieldsByName[MAPPED_1_SEC_SPECTRAL_ACC]} />
            </Col>
          </FormGroup>
          <FormGroup form={form} fields={[DESIGN_SHORT_PERIOD_SPECTRAL_ACC]} row>
            <Col lg="6">
              <Label field={fieldsByName[DESIGN_SHORT_PERIOD_SPECTRAL_ACC]}>
                <span>
                  Design Short Period Spectral Acceleration (S
                  <small>DS</small>)
                </span>
              </Label>
              <Field excludeLabel field={fieldsByName[DESIGN_SHORT_PERIOD_SPECTRAL_ACC]} />
            </Col>
          </FormGroup>
          <FormGroup form={form} fields={[DESIGN_1_SEC_SPECTRAL_ACC]} row>
            <Col lg="6">
              <Label field={fieldsByName[DESIGN_1_SEC_SPECTRAL_ACC]}>
                <span>
                  Design 1-Second Spectral Acceleration (S
                  <small>D1</small>)
                </span>
              </Label>
              <Field excludeLabel field={fieldsByName[DESIGN_1_SEC_SPECTRAL_ACC]} />
            </Col>
          </FormGroup>
        </Fieldset>
        <Fieldset
          form={form}
          fields={[
            INCLUDE_RETROFIT,
            STRUCTURAL_RETROFIT_YEAR,
            STRUCTURAL_RETROFIT_CODE_YEAR,
            STRUCTURAL_RETROFIT_LEVEL,
            NON_STRUCTURAL_RETROFIT_YEAR,
            NON_STRUCTURAL_RETROFIT_CODE_YEAR,
            NON_STRUCTURAL_RETROFIT_LEVEL,
          ]}
        >
          <legend>Retrofit Information</legend>
          <FormGroup form={form} fields={[INCLUDE_RETROFIT]} row>
            <Col lg="5">
              <Field field={fieldsByName[INCLUDE_RETROFIT]} />
            </Col>
          </FormGroup>
          <FormGroup
            form={form}
            fields={[
              STRUCTURAL_RETROFIT_YEAR,
              STRUCTURAL_RETROFIT_CODE_YEAR,
              STRUCTURAL_RETROFIT_LEVEL,
            ]}
          >
            <Row>
              <Col lg="5">
                <h5>Structural Retrofit</h5>
              </Col>
            </Row>
            <Row>
              <Col lg="5">
                <Field field={fieldsByName[STRUCTURAL_RETROFIT_YEAR]} />
                <Field field={fieldsByName[STRUCTURAL_RETROFIT_CODE_YEAR]} />
                <FieldVisibilityToggle
                  id="structuralRetrofitYearButton"
                  form={form}
                  on={structuralRetrofitYearIsVisible}
                  onFields={[STRUCTURAL_RETROFIT_YEAR]}
                  onText="Use design Code?"
                  offFields={[STRUCTURAL_RETROFIT_CODE_YEAR]}
                  offText="Use year of construction?"
                />
              </Col>
              <Col lg="5">
                <Field field={fieldsByName[STRUCTURAL_RETROFIT_LEVEL]} />
              </Col>
            </Row>
          </FormGroup>
          <FormGroup
            form={form}
            fields={[
              NON_STRUCTURAL_RETROFIT_YEAR,
              NON_STRUCTURAL_RETROFIT_CODE_YEAR,
              NON_STRUCTURAL_RETROFIT_LEVEL,
            ]}
          >
            <Row>
              <Col lg="5">
                <h5>Non-Structural Retrofit</h5>
              </Col>
            </Row>
            <Row>
              <Col lg="5">
                <Field field={fieldsByName[NON_STRUCTURAL_RETROFIT_YEAR]} />
                <Field field={fieldsByName[NON_STRUCTURAL_RETROFIT_CODE_YEAR]} />
                <FieldVisibilityToggle
                  id="nonStructuralRetrofitYearButton"
                  form={form}
                  on={nonstructuralRetrofitYearIsVisible}
                  onFields={[NON_STRUCTURAL_RETROFIT_YEAR]}
                  onText="Use design Code?"
                  offFields={[NON_STRUCTURAL_RETROFIT_CODE_YEAR]}
                  offText="Use year of construction?"
                />
              </Col>
              <Col lg="5">
                <Field field={fieldsByName[NON_STRUCTURAL_RETROFIT_LEVEL]} />
              </Col>
            </Row>
          </FormGroup>
        </Fieldset>
        <BackButton onClick={onBack} />
        <NextButton onClick={onNext} />
      </Form>
    </div>
  );
};

export default BuildingDesignInfoSection;
